import React, {useState} from 'react';
import {theme} from '@styles/theme';
import Text from '@atoms/Text';
import CoverageBlock from '@molecules/CoverageBlock';
import {AddAlt, ChevronSort, Edit} from '@carbon/icons-react';
import {
  AddIcon,
  CoverageDetailBlock,
  CoverageDetails,
  EditButton,
  HeaderContent,
  ModuleCellContent,
  ModuleName,
  ModuleNameWithEdit,
  NotAvailable,
  SortIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TestCellContent,
} from './ModulesTable.styles';
import {PageSubtitleLegendMark} from '@organisms/PageSubtitleBlock.styles';
import {ICell, IModule} from '@types';
import {EUserRoles, EUserScopedRoles} from '@constants/usersRoles';

interface ModulesTableProps {
  modules: IModule[];
  onAddModule: () => void;
  onEditModule: (module: IModule) => void;
  onEditTest: (module: IModule, cell: ICell) => void;
  onSortTests: (testId: number) => void;
  readOnly?: boolean;
  systemRole?: number;
  scopedRole?: number;
}

const ModulesTable: React.FC<ModulesTableProps> = ({
  modules,
  onAddModule,
  onEditModule,
  onEditTest,
  onSortTests,
  readOnly = false,
  systemRole = 2,
  scopedRole = 1,
}) => {
  const [hoveredModule, setHoveredModule] = useState<number | null>(null);
  const [hoveredTest, setHoveredTest] = useState<{
    moduleId: number;
    testId: number;
  } | null>(null);

  // Получаем все уникальные тесты из всех модулей
  const allTests = Array.from(
    new Set(modules.flatMap(module => module.cells?.map(cell => cell.test_type_id))),
  );

  // Создаем массив тестов с их названиями (предполагаем, что тесты с одинаковым id имеют одинаковые имена)
  const testNames = modules.reduce<Record<number, string>>(
    (acc, module) => {
      module.cells?.forEach(cell => {
        if (acc[cell.test_type_id] === undefined) {
          acc[cell.test_type_id] = cell.test_type_name;
        }
      });
      return acc;
    },
    {} as Record<number, string>,
  );

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>
              <HeaderContent>
                <Text text="Список модулей" type="bodyM" />
                {!readOnly &&
                  (systemRole !== EUserRoles.BASE_USER ||
                    scopedRole !== EUserScopedRoles.VIEWER) && (
                    <AddIcon onClick={onAddModule}>
                      <AddAlt name="plus" size={14} color={theme.colors.basic00} />
                    </AddIcon>
                  )}
              </HeaderContent>
            </TableHeaderCell>

            {allTests.map(testId => (
              <TableHeaderCell key={`header-${testId}`}>
                <HeaderContent>
                  <Text text={testNames[Number(testId)] || ''} type="bodyM" />
                  {!readOnly && (
                    <SortIcon onClick={() => onSortTests(Number(testId))}>
                      <ChevronSort name="sort" size={12} color={theme.colors.basic02} />
                    </SortIcon>
                  )}
                </HeaderContent>
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {modules.map((module, idx) => (
            <TableRow
              key={module.id}
              onMouseEnter={() => setHoveredModule(module.id)}
              onMouseLeave={() => setHoveredModule(null)}>
              <TableCell>
                <ModuleCellContent>
                  {hoveredModule != module.id && (
                    <ModuleName>
                      <Text text={module.name} />
                      <Text
                        text={`${Math.round(module.coverage + module.auto_coverage)}%`}
                        type="bodyNum"
                      />
                    </ModuleName>
                  )}
                  {hoveredModule === module.id && (
                    <>
                      <ModuleNameWithEdit>
                        <Text text={module.name} />
                        <Text
                          text={`${Math.round(module.coverage + module.auto_coverage)}%`}
                          type="bodyNum"
                        />
                        {!readOnly &&
                          (systemRole !== EUserRoles.BASE_USER ||
                            scopedRole !== EUserScopedRoles.VIEWER) && (
                            <EditButton onClick={() => onEditModule(module)}>
                              <Edit name="edit" size={16} color={theme.colors.accent00} />
                            </EditButton>
                          )}
                      </ModuleNameWithEdit>
                      <CoverageBlock
                        autoCoverage={module.auto_coverage}
                        manualCoverage={module.coverage}
                        forTable
                        tooltipOnTop={modules.length == idx + 1}
                      />
                    </>
                  )}
                </ModuleCellContent>
              </TableCell>

              {allTests.map(testId => {
                const moduleTest = module.cells?.find(t => t.test_type_id === testId);

                return (
                  <TableCell
                    key={`${module.id}-${testId}`}
                    onMouseEnter={() =>
                      moduleTest &&
                      setHoveredTest({moduleId: module.id, testId: Number(testId)})
                    }
                    onMouseLeave={() => setHoveredTest(null)}>
                    {moduleTest ? (
                      <TestCellContent>
                        <Text
                          text={`${(moduleTest.coverage + moduleTest.auto_coverage).toFixed(2).replace('.', ',') || (0).toFixed(2).replace('.', ',')}%`}
                          type="bodyNum"
                        />

                        {hoveredTest?.moduleId === module.id &&
                          hoveredTest?.testId === testId &&
                          !readOnly &&
                          (systemRole !== EUserRoles.BASE_USER ||
                            scopedRole !== EUserScopedRoles.VIEWER) && (
                            <EditButton onClick={() => onEditTest(module, moduleTest)}>
                              <Edit name="edit" size={16} color={theme.colors.accent00} />
                            </EditButton>
                          )}

                        {hoveredTest?.moduleId === module.id &&
                          hoveredTest?.testId === testId && (
                            <CoverageDetails>
                              <CoverageDetailBlock>
                                <PageSubtitleLegendMark color={theme.colors.manual00} />
                                <Text
                                  text={`${moduleTest.coverage?.toFixed(2) || (0).toFixed(2)}%`}
                                  type="tinyNum"
                                  color={theme.colors.basic03}
                                />
                              </CoverageDetailBlock>
                              <CoverageDetailBlock>
                                <PageSubtitleLegendMark color={theme.colors.auto00} />
                                <Text
                                  text={`${moduleTest.auto_coverage?.toFixed(2) || (0).toFixed(2)}%`}
                                  type="tinyNum"
                                  color={theme.colors.basic03}
                                />
                              </CoverageDetailBlock>
                            </CoverageDetails>
                          )}
                      </TestCellContent>
                    ) : (
                      <NotAvailable>
                        <Text text="N/A" type="bodyNum" color={theme.colors.basic03} />
                      </NotAvailable>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ModulesTable;
